import { TerminalEntry } from '../types/terminal';

interface CommandOutputProps {
  entries: TerminalEntry[];
  actionsDisabled?: boolean;
  onActionCommand?: (command: string) => void;
  typingCursor?: {
    entryId: number;
    lineIndex: number;
  } | null;
}

export function CommandOutput({ entries, actionsDisabled = false, onActionCommand, typingCursor = null }: CommandOutputProps) {
  return (
    <div className="terminal-output" aria-live="polite">
      {entries.map((entry) => {
        if (entry.type === 'input') {
          return (
            <p key={entry.id} className="terminal-line terminal-input-echo">
              <span className="prompt">{entry.prompt}</span> {entry.value}
            </p>
          );
        }

        if (entry.type === 'links') {
          return (
            <div key={entry.id} className="terminal-block">
              {entry.title ? <p className="terminal-line">{entry.title}</p> : null}
              {entry.links.map((link) => (
                <p key={link.label} className="terminal-line">
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </p>
              ))}
            </div>
          );
        }

        if (entry.type === 'neofetch') {
          const artLines = entry.rows.map((row) => row.left || ' ');
          const stats = entry.rows.filter((row) => row.label || row.value);

          return (
            <div key={entry.id} className="terminal-block neofetch-block">
              <pre className="neofetch-art" aria-hidden="true">
                {artLines.join('\n')}
              </pre>
              <div className="neofetch-stats">
                {stats.map((row, rowIndex) => (
                  <p key={`${entry.id}-stat-${rowIndex}`} className="terminal-line neofetch-line">
                    {row.label ? (
                      <>
                        <span className="neofetch-label">{row.label}</span>
                        <span className="neofetch-value"> {row.value}</span>
                      </>
                    ) : (
                      <span className="neofetch-value">{row.value}</span>
                    )}
                  </p>
                ))}
              </div>
            </div>
          );
        }

        if (entry.type === 'actions') {
          return (
            <div key={entry.id} className="terminal-block">
              {entry.title ? <p className="terminal-line">{entry.title}</p> : null}
              <div className="command-buttons command-buttons-inline">
                {entry.actions.map((action) => (
                  <button
                    key={`${entry.id}-${action.command}`}
                    type="button"
                    disabled={actionsDisabled || !onActionCommand}
                    onClick={() => onActionCommand?.(action.command)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          );
        }

        if (entry.type === 'projects') {
          return (
            <div key={entry.id} className="terminal-block">
              {entry.title ? <p className="terminal-line">{entry.title}</p> : null}
              {entry.projects.map((project) => (
                <div key={`${entry.id}-${project.name}`} className="terminal-project">
                  <p className="terminal-line">{project.name}</p>
                  <p className="terminal-line">{project.summary}</p>
                  {project.url ? (
                    <p className="terminal-line">
                      {project.linkLabel ? `${project.linkLabel}: ` : null}
                      <a href={project.url} target="_blank" rel="noreferrer">
                        {project.url}
                      </a>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          );
        }

        return (
          <div key={entry.id} className="terminal-block">
            {entry.lines.map((line, lineIndex) => (
              <p
                key={`${entry.id}-${lineIndex}`}
                className={`terminal-line ${line.tone ? `tone-${line.tone}` : ''}`.trim()}
              >
                {line.text}
                {typingCursor?.entryId === entry.id && typingCursor.lineIndex === lineIndex ? (
                  <span className="terminal-cursor active inline-cursor" aria-hidden="true" />
                ) : null}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}
